/**
 * O catálogo de comandos ("/") do `send-message`.
 *
 * Extraído do componente porque `send-message.vue` passava do teto de 500
 * linhas do repo (achado do review de design-system do PR #2041) — escopo
 * mínimo (Opção A da investigação): só o computed e seu filtro. Os métodos
 * `comandoXxx`/gatilhos de detecção continuam no host e resolvem via `this`
 * compartilhado — mixin não é dado estático, lê `this.$listeners`,
 * `this.showAgentSelect`, `this.escopoDeCliente`, `this.hideModelSelect`,
 * `this.mostraThinkingToggle`, `this.thinkingEnabled`, `this.showAttachments`,
 * `this.rotuloAnexos`, `this.anexosDesabilitados`.
 *
 * [spec-013 T003] `sendMessageAtalhosMixin` entra aqui, ANINHADO — e não no
 * array `mixins` de `send-message.vue` — porque `send-message.vue` está
 * sob a regra de "uma função só" desta task (2.670 linhas, outra sessão
 * mexendo no arquivo). Vue 2 mescla `mixins` recursivamente, então o host
 * ganha a prop `atalhos` e os dois computeds do atalho sem precisar ser
 * tocado. `catalogoDeComandos` concatena a seção — ADITIVO, os 8 comandos
 * de hoje continuam intactos e na mesma ordem.
 */
import { sendMessageAtalhosMixin } from './send-message-atalhos-mixin'

export const sendMessageCatalogoComandosMixin = {
  mixins: [sendMessageAtalhosMixin],
  computed: {
    /**
     * Os comandos do `/`. Cada um é uma ação do COMPOSER, não um módulo da IA —
     * por isso vivem aqui, que é onde estão os refs e o store, e não no
     * `composer-ferramentas`, que só sabe desenhar a lista.
     *
     * `aliases` cobre quem vem de outro chat e digita em inglês (`/new`,
     * `/clear`, `/agent`).
     *
     * ⚠️ Cada entrada carrega o próprio `mostrar`, e o gate fica ao lado do
     * comando que ele governa. Os que dependem de um chip só entram quando o
     * chip EXISTE: no modo limpo (a tela imersiva `/ia`) o `modeloOculto`
     * derruba o trio modelo + hint + raciocínio, e oferecer o comando ali
     * abriria um menu que não está na tela.
     */
    catalogoDeComandos() {
      return [
        {
          // Só quando a superfície hospedeira TRATA o evento. `agent-chat` e
          // `fee-fixo-ia-especialista` montam este composer sem conversa
          // própria — lá o comando abriria caminho para lugar nenhum.
          mostrar: Boolean(this.$listeners['nova-conversa']),
          label: 'Nova conversa',
          description: 'Começa um chat do zero',
          // Mesmo ícone que a linha "Nova conversa" da sidebar usa — é a mesma
          // ação, e duas caras para ela seriam duas coisas diferentes.
          icon: 'add-circle',
          aliases: ['nova', 'new', 'clear'],
          onClick: this.comandoNovaConversa,
        },
        {
          mostrar: this.showAgentSelect,
          label: 'Agente',
          description: 'Escolhe quem responde',
          icon: 'robot',
          aliases: ['agent'],
          onClick: this.comandoAgente,
        },
        {
          mostrar: this.escopoDeCliente,
          label: 'Cliente',
          description: 'Define o cliente em foco',
          icon: 'user-2',
          aliases: ['client', 'escopo'],
          onClick: this.comandoCliente,
        },
        {
          // Só com o agente que sabe o que fazer com o arquivo — ver a prop
          // `anexoDeCarteiraLiberado`.
          mostrar: this.anexoDeCarteiraLiberado,
          label: 'Anexar carteira',
          description: 'Sobe o extrato do cliente para a IA ler',
          icon: 'document-upload',
          aliases: ['carteira', 'extrato', 'anexo', 'pdf'],
          onClick: this.comandoAnexarCarteira,
        },
        {
          // ⚠️ `hideModelSelect`, e NÃO `modeloOculto`. Os dois motivos de o
          // chip sumir são diferentes: `hideModelSelect` é a superfície que
          // genuinamente não escolhe modelo (o ai-framework, RF2.6/NC-3) — ali
          // o comando não pode existir; `estiloLimpo` é só a fileira da `/ia`
          // que não comporta o chip, e lá o modelo segue trocável pelo palette.
          // Somando os dois, o `/modelo` sumia da `/ia` sem motivo.
          mostrar: !this.hideModelSelect,
          label: 'Modelo',
          description: 'Troca o modelo da resposta',
          icon: 'ia-head',
          aliases: ['model'],
          onClick: this.comandoModelo,
        },
        {
          mostrar: this.mostraThinkingToggle,
          label: 'Raciocínio',
          description: this.thinkingEnabled ? 'Ligado' : 'Desligado',
          // O mesmo `mdi-brain` do chip de raciocínio, logo ao lado na barra.
          icon: 'mdi-brain',
          active: this.thinkingEnabled,
          aliases: ['raciocinio', 'thinking', 'reasoning'],
          onClick: this.comandoRaciocinio,
        },
        {
          mostrar: true,
          label: 'Buscar conversas',
          description: 'Abre a busca global',
          icon: 'search-normal',
          aliases: ['buscar', 'search'],
          onClick: this.comandoBuscar,
        },
        {
          // Mesmo gate do "+": onde o anexo não aparece lá, não aparece aqui.
          // Segue DESABILITADO enquanto o envio não tiver canal para arquivo —
          // a linha existe para a função ser descoberta, não para prometer o
          // que ainda não funciona (ver `anexosDesabilitados`).
          mostrar: this.showAttachments,
          label: 'Anexar arquivos',
          description: this.rotuloAnexos,
          icon: 'paperclip',
          disabled: this.anexosDesabilitados,
          aliases: ['anexar', 'attach', 'arquivo'],
          // ⚠️ `comandoAnexar`, e NÃO `abrirAnexos` direto: o `/` digitado fica
          // no campo até alguém apagá-lo, e o menu do "+" chama o mesmo
          // `abrirAnexos` SEM ter barra nenhuma para limpar. São dois caminhos
          // para a mesma ação com limpezas diferentes.
          onClick: this.comandoAnexar,
        },
      ].concat(this.catalogoDeAtalhos)
    },
    comandosDoComposer() {
      return this.catalogoDeComandos.filter((comando) => comando.mostrar)
    },
  },
}
